import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const APP_NAME = "Panel Doradcy";
const ADMIN_APP_NAME = "Panel Administratora";

/**
 * Route title configuration for advisor panel
 */
export const advisorRouteTitles: Record<string, string> = {
  "/": "Dashboard",
  "/leads": "Leady",
  "/sessions": "Sesje",
  "/templates": "Szablony",
  "/tickets": "Moje zgłoszenia",
  "/profile": "Profil",
  "/login": "Logowanie",
  "/register": "Rejestracja",
};

/**
 * Route title configuration for admin panel
 */
export const adminRouteTitles: Record<string, string> = {
  "/admin": "Dashboard",
  "/admin/advisors": "Doradcy",
  "/admin/audit-logs": "Historia zmian",
  "/admin/login": "Logowanie administratora",
};

/**
 * Dynamic route patterns for session detail pages
 */
const dynamicRoutePatterns: Array<{ pattern: RegExp; title: string }> = [
  { pattern: /^\/sessions\/[^/]+$/, title: "Szczegóły sesji" },
  { pattern: /^\/doradca\/[^/]+$/, title: "Profil doradcy" },
];

/**
 * Get page title for a given path
 */
export function getPageTitle(pathname: string): string {
  // Check advisor routes
  if (advisorRouteTitles[pathname]) {
    return advisorRouteTitles[pathname];
  }

  // Check admin routes
  if (adminRouteTitles[pathname]) {
    return adminRouteTitles[pathname];
  }

  // Check dynamic route patterns
  for (const { pattern, title } of dynamicRoutePatterns) {
    if (pattern.test(pathname)) {
      return title;
    }
  }

  // Fallback
  return "Panel";
}

/**
 * Check if current path is an admin route
 */
function isAdminRoute(pathname: string): boolean {
  return pathname.startsWith("/admin");
}

/**
 * Format full document title with app name
 */
export function formatDocumentTitle(pageTitle: string, pathname: string): string {
  const appName = isAdminRoute(pathname) ? ADMIN_APP_NAME : APP_NAME;

  if (pageTitle === "Dashboard") {
    return appName;
  }

  return `${pageTitle} | ${appName}`;
}

/**
 * Hook to set document title based on current route
 * Automatically updates when location changes
 */
export function useDocumentTitle(customTitle?: string): void {
  const location = useLocation();

  useEffect(() => {
    const pageTitle = customTitle || getPageTitle(location.pathname);
    document.title = formatDocumentTitle(pageTitle, location.pathname);
  }, [location.pathname, customTitle]);
}

/**
 * Component that updates document title based on route
 * Can be placed in the app to automatically track title changes
 */
export function DocumentTitleTracker(): null {
  useDocumentTitle();
  return null;
}
